/**
 * Lobbero motion library — shared @emotion/react keyframes and helpers for the
 * PURE-CSS sites: hover, press, ambient loops, and anything that is styled but
 * never mounts or unmounts.
 *
 *   import { fadeInUp } from '../../style-utils/motion';
 *   const Card = styled.div`
 *     animation: ${fadeInUp} ${duration.base} ${easing.entrance} both;
 *   `;
 *
 * ANYTHING THAT MOUNTS OR UNMOUNTS — a modal, a drawer, a collapse, a list row
 * that can be removed, a skeleton giving way to content, a page — lives in
 * src/motion (framer-motion): Presence, Collapse, StateSwitch, StaggerList,
 * the overlay helpers and the route layer. A CSS keyframe cannot animate an
 * element OUT (the element is gone before the animation starts), which is why
 * the exit keyframes below exist only for the handful of sites that toggle a
 * class on an element that stays mounted.
 *
 * Durations and easings come from ./theme (`duration.*`, `easing.*`) — the
 * CSS-string twins of src/motion/tokens.ts. Ambient loops use `LOOP` below:
 * they never settle, so the transition scale's "how long until it lands" does
 * not apply to them.
 *
 * All animations respect `prefers-reduced-motion` via the global rule in
 * index.css (durations and delays collapse to ~0 for users who opt out); do
 * not add per-component reduced-motion blocks.
 */
import { keyframes } from '@emotion/react';
import { css } from '@emotion/react';
import { duration, easing } from './theme';

/* Entrances ------------------------------------------------------------- */

export const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const fadeInUp = keyframes`
  from { opacity: 0.94; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const fadeInDown = keyframes`
  from { opacity: 0.94; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const scaleIn = keyframes`
  from { opacity: 0.94; transform: scale(0.995); }
  to { opacity: 1; transform: scale(1); }
`;

export const slideInRight = keyframes`
  from { opacity: 0.94; transform: translateX(8px); }
  to { opacity: 1; transform: translateX(0); }
`;

export const slideInLeft = keyframes`
  from { opacity: 0.94; transform: translateX(-8px); }
  to { opacity: 1; transform: translateX(0); }
`;

/* Wizard step transitions use short directional travel and begin close to
   fully opaque so changing steps feels continuous instead of flashing. */
export const wizardSlideInRight = keyframes`
  from { opacity: 0.94; transform: translateX(12px); }
  45% { opacity: 1; }
  to { opacity: 1; transform: translateX(0); }
`;

export const wizardSlideInLeft = keyframes`
  from { opacity: 0.94; transform: translateX(-12px); }
  45% { opacity: 1; }
  to { opacity: 1; transform: translateX(0); }
`;

/* Section switches (visitor-log <-> daily-log) — the vertical cousin of the
   wizard slide: visitor->daily sweeps in top-to-bottom, daily->visitor
   bottom-to-top. */
export const sectionSlideInDown = keyframes`
  from { opacity: 0.94; transform: translateY(-8px); }
  45% { opacity: 1; }
  to { opacity: 1; transform: translateY(0); }
`;

export const sectionSlideInUp = keyframes`
  from { opacity: 0.94; transform: translateY(8px); }
  45% { opacity: 1; }
  to { opacity: 1; transform: translateY(0); }
`;

/* Exits ----------------------------------------------------------------- */
/* For elements that STAY MOUNTED and toggle a class (a toast fading before it
   is removed on a timer, a badge emptying). An element that unmounts cannot be
   animated with CSS at all — use Presence from src/motion for that. */

export const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

export const scaleOut = keyframes`
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.98); }
`;

export const fadeOutDown = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(4px); }
`;

/* Feedback -------------------------------------------------------------- */

export const pop = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(0.94); }
  100% { transform: scale(1); }
`;

export const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(6px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
`;

export const checkPop = keyframes`
  0% { transform: scale(0.6); opacity: 0; }
  60% { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

/* Ambient / looping ----------------------------------------------------- */

export const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const shimmer = keyframes`
  0% { background-position: -480px 0; }
  100% { background-position: 480px 0; }
`;

/* Aurora — for AI ("Lobbero IQ") surfaces only. Gentle hue drift + glow. */
export const auroraShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

export const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 161, 154, 0.35); }
  50% { box-shadow: 0 0 0 10px rgba(0, 161, 154, 0); }
`;

/* Ambient loop durations. Not on the transition scale on purpose: a loop
   never settles, so `duration.*` (which answers "how long until it lands") is
   the wrong vocabulary for it. Every shimmer, spinner and aurora drift reads
   its period from here so the app has ONE shimmer speed. */
export const LOOP = {
  /** Skeleton shimmer sweep. */
  shimmer: '1400ms',
  /** The legacy SVG spinner's rotation and dash cycle. */
  spinner: '1400ms',
  /** The legacy SVG spinner's hue cycle — four rotations per hue loop. */
  spinnerHue: '5600ms',
  /** Aurora hue drift on a running AI header. */
  aurora: '3000ms',
} as const;

/* Helper: the standard skeleton shimmer animation, one speed everywhere.
   Pair with `skeletonBackground` below. */
export const shimmerLoop = css`
  animation: ${shimmer} ${LOOP.shimmer} ${easing.standard} infinite;
`;

/* Helper: skeleton shimmer background (pair with the `shimmer` keyframe). */
export const skeletonBackground = `
  background: linear-gradient(
    90deg,
    rgba(220, 232, 231, 0.45) 25%,
    rgba(198, 216, 214, 0.72) 37%,
    rgba(220, 232, 231, 0.45) 63%
  );
  background-size: 960px 100%;
`;

/* Helper: standard staggered list-item entrance delay (index-based). */
export const stagger = (index: number, step = 45): string =>
  `${index * step}ms`;

export const springyEntrance = `${easing.entrance}`;

/* Re-exported so a styled file can name the scale without a second import. */
export { duration };
